/**
 * Cookie choices for app.blueticks.co (spec 23 section 8). Opt-in and unknown locations (EU/EEA, UK,
 * CH, IL, BR, TR, Quebec; unknown = deny) see the banner whenever there is no current stored choice,
 * on every page including the login page. Accept all and Reject all have equal weight. Closing the
 * banner with Esc records nothing. Opt-out locations get the defaults with no banner, logged once per
 * session; the settings panel (account menu, login footer) lets them opt out. The panel opens through
 * CONSENT_OPEN_EVENT.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch } from "@mui/material";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { selectUserObject } from "@common-reducers/UserSelectors";
import {
  buildStoredConsent,
  makeChoices,
  type ConsentChoices,
  type ConsentSource,
} from "@common/services/consent/contract";
import {
  CONSENT_OPEN_EVENT,
  currentConsent,
  effectiveConsent,
  newConsentId,
  readStoredConsent,
  writeStoredConsent,
} from "@common/services/consent/store";
import { currentJurisdiction, geoForLog } from "@common/services/consent/jurisdiction";
import { anyVendorLoaded, onConsentChange, postConsentLog, withdrawTrackers } from "@common/services/consent/loaders";

const copy = {
  bannerTitle: "Your privacy choices",
  bannerBody:
    "We use cookies and similar tools to keep the site working, to understand how it is used, and to measure our advertising. Only the ones needed to run the site are on by default. You can accept all, reject all, or choose.",
  acceptAll: "Accept all",
  rejectAll: "Reject all",
  choose: "Choose",
  privacyPolicy: "Privacy Policy",
  settingsTitle: "Cookie settings",
  necessaryTitle: "Strictly necessary (always on)",
  necessaryBody: "Sign-in, security, and remembering your choices.",
  analyticsTitle: "Analytics",
  analyticsBody:
    "Helps us see which pages and features are used. Includes Google Analytics, a device identifier created from your browser's characteristics, and Hotjar session analytics.",
  marketingTitle: "Marketing",
  marketingBody: "Lets us measure our advertising (Meta Pixel and TikTok) and credit affiliate referrals.",
  save: "Save choices",
  settingsFooter: 'You can change your mind at any time using "Cookie settings" in the account menu.',
};

/** sessionStorage flag: the opt-out default is logged once per browser session, not on every page view. */
const DEFAULT_LOGGED_KEY = "bt_consent_default_logged";

function logDefaultOnce(source: string, log: () => void): void {
  try {
    if (sessionStorage.getItem(DEFAULT_LOGGED_KEY) === source) return;
    sessionStorage.setItem(DEFAULT_LOGGED_KEY, source);
  } catch {
    /* Storage denied: may log more than once per session. Acceptable. */
  }
  log();
}

/** Opens the settings panel from anywhere: the account menu and the login page footer. */
export function openCookieSettings(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}

export default function CookieConsentBanner() {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUserObject)?._id as string | undefined;
  const [hasChoice, setHasChoice] = useState<boolean>(() => currentConsent() !== null);
  // Opt-out locations never see the banner: they get the default grant and can open the settings panel.
  const [optOut] = useState<boolean>(() => currentJurisdiction().jurisdiction === "opt_out");
  const [dismissed, setDismissed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState<{ analytics: boolean; marketing: boolean }>({ analytics: false, marketing: false });
  const regionRef = useRef<HTMLDivElement>(null);

  // Opt-out default: log the grant once per session (no stored choice, nothing persisted). The once-per-session
  // guard lives in sessionStorage, so re-running when userId arrives does not log twice.
  useEffect(() => {
    if (currentConsent()) return;
    const eff = effectiveConsent();
    if (eff.decision.jurisdiction !== "opt_out" || !eff.source) return;
    const source = eff.source;
    logDefaultOnce(source, () =>
      postConsentLog({ consentId: newConsentId(), choices: eff.choices, source, userId, geo: geoForLog(eff.decision) }),
    );
  }, [userId]);

  useEffect(() => {
    const refresh = () => setHasChoice(currentConsent() !== null);
    const off = onConsentChange(refresh);
    const onOpen = () => {
      // Seeded from the effective choice, so an opt-out visitor sees analytics ON and can switch it off.
      const c = effectiveConsent().choices;
      setDraft({ analytics: c.analytics, marketing: c.marketing });
      setSettingsOpen(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    return () => {
      off();
      window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
    };
  }, []);

  const save = useCallback(
    (choices: ConsentChoices, source: ConsentSource) => {
      // Compare against what is APPLIED now (stored choice or jurisdiction default), so an opt-out
      // visitor switching analytics off is a real withdrawal: trackers are torn down and the page reloads.
      const eff = effectiveConsent();
      const previous = eff.choices;
      const previousStored = readStoredConsent();
      const consentId = previousStored?.consentId ?? newConsentId();
      const turnedOff = (previous.analytics && !choices.analytics) || (previous.marketing && !choices.marketing);
      // Only a real withdrawal is logged as one; each button supplies its own source otherwise.
      const logSource: ConsentSource = turnedOff ? "settings_withdraw" : source;
      if (!choices.analytics || !choices.marketing) withdrawTrackers();
      writeStoredConsent(buildStoredConsent({ consentId, choices, now: new Date() }));
      postConsentLog({ consentId, choices, source: logSource, userId, geo: geoForLog(eff.decision) });
      setSettingsOpen(false);
      setHasChoice(true);
      // Third-party scripts cannot be unloaded from the page: reload if one was live and is now off.
      if (turnedOff && anyVendorLoaded()) window.location.reload();
    },
    [userId],
  );

  // Esc hides the banner without recording a choice. Tab stays inside the banner (focus trap),
  // because the banner is a non-modal region; the settings Dialog is modal and traps focus itself.
  const onRegionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setDismissed(true);
      return;
    }
    if (e.key !== "Tab" || !regionRef.current) return;
    const items = Array.from(regionRef.current.querySelectorAll<HTMLElement>("button, a[href]"));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !regionRef.current.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !regionRef.current.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  const showBanner = !hasChoice && !optOut && !dismissed && !settingsOpen;

  return (
    <>
      {showBanner && (
        <div
          ref={regionRef}
          role="region"
          aria-label={t("cookieConsent.regionLabel", { defaultValue: copy.bannerTitle })}
          onKeyDown={onRegionKeyDown}
          data-testid="cookie-consent-banner"
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            padding: "16px",
            background: "var(--color-bg, #fff)",
            color: "var(--color-text, #111)",
            borderTop: "1px solid rgba(0,0,0,0.12)",
            boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ maxWidth: 960, margin: "0 auto" }}>
            <strong>{t("cookieConsent.bannerTitle", { defaultValue: copy.bannerTitle })}</strong>
            <p style={{ margin: "8px 0" }}>{t("cookieConsent.bannerBody", { defaultValue: copy.bannerBody })}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <Button variant="contained" size="medium" onClick={() => save(makeChoices(true, true), "banner_accept_all")}>
                {t("cookieConsent.acceptAll", { defaultValue: copy.acceptAll })}
              </Button>
              <Button variant="contained" size="medium" onClick={() => save(makeChoices(false, false), "banner_reject_all")}>
                {t("cookieConsent.rejectAll", { defaultValue: copy.rejectAll })}
              </Button>
              <Button variant="outlined" size="medium" onClick={() => setSettingsOpen(true)}>
                {t("cookieConsent.choose", { defaultValue: copy.choose })}
              </Button>
              <a href="https://blueticks.co/privacy-policy" target="_blank" rel="noopener noreferrer">
                {t("cookieConsent.privacyPolicy", { defaultValue: copy.privacyPolicy })}
              </a>
            </div>
          </div>
        </div>
      )}

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} aria-labelledby="cookie-settings-title" fullWidth maxWidth="sm">
        <DialogTitle id="cookie-settings-title">{t("cookieConsent.settingsTitle", { defaultValue: copy.settingsTitle })}</DialogTitle>
        <DialogContent>
          <FormControlLabel control={<Switch checked disabled />} label={t("cookieConsent.necessaryTitle", { defaultValue: copy.necessaryTitle })} />
          <p style={{ margin: "0 0 12px" }}>{t("cookieConsent.necessaryBody", { defaultValue: copy.necessaryBody })}</p>
          <FormControlLabel
            control={<Switch checked={draft.analytics} onChange={(_, v) => setDraft({ ...draft, analytics: v })} />}
            label={t("cookieConsent.analyticsTitle", { defaultValue: copy.analyticsTitle })}
          />
          <p style={{ margin: "0 0 12px" }}>{t("cookieConsent.analyticsBody", { defaultValue: copy.analyticsBody })}</p>
          <FormControlLabel
            control={<Switch checked={draft.marketing} onChange={(_, v) => setDraft({ ...draft, marketing: v })} />}
            label={t("cookieConsent.marketingTitle", { defaultValue: copy.marketingTitle })}
          />
          <p style={{ margin: "0 0 12px" }}>{t("cookieConsent.marketingBody", { defaultValue: copy.marketingBody })}</p>
          <p style={{ fontSize: 12, opacity: 0.8 }}>{t("cookieConsent.settingsFooter", { defaultValue: copy.settingsFooter })}</p>
        </DialogContent>
        <DialogActions style={{ flexWrap: "wrap", gap: 8, padding: "8px 16px 16px" }}>
          <Button variant="outlined" onClick={() => save(makeChoices(false, false), "banner_reject_all")}>
            {t("cookieConsent.rejectAll", { defaultValue: copy.rejectAll })}
          </Button>
          <Button variant="outlined" onClick={() => save(makeChoices(true, true), "banner_accept_all")}>
            {t("cookieConsent.acceptAll", { defaultValue: copy.acceptAll })}
          </Button>
          <Button variant="contained" onClick={() => save(makeChoices(draft.analytics, draft.marketing), "banner_custom")}>
            {t("cookieConsent.save", { defaultValue: copy.save })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
